import React, { useState, useEffect } from 'react';
import styles from '../../styles/pages/Contact.module.css';

/**
 * Contact form component using direct Formspree API integration
 * This implementation avoids dependency issues with @formspree/react
 * Enhanced with improved validation and rate limiting
 */
export function ContactForm() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: '',
    isSubmitting: false,
    isSuccess: false,
    error: null,
    submitCount: 0,
    lastSubmitTime: 0
  });

  // Load submission history from session storage
  useEffect(() => {
    const storedData = sessionStorage.getItem('formSubmissionData');
    if (storedData) {
      const { submitCount, lastSubmitTime } = JSON.parse(storedData);
      setFormState(prev => ({
        ...prev,
        submitCount,
        lastSubmitTime
      }));
    }
  }, []);

  // Input validation function
  const validateInputs = () => {
    // Email regex pattern
    const emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    
    if (!formState.name.trim()) {
      return "Name is required";
    }
    
    if (!emailPattern.test(formState.email)) {
      return "Please enter a valid email address";
    }
    
    if (!formState.message.trim() || formState.message.length < 10) {
      return "Please enter a message (at least 10 characters)";
    }
    
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value,
      // Clear error when user starts typing again
      error: null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Rate limiting check (3 submissions per hour)
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    
    if (formState.submitCount >= 3 && now - formState.lastSubmitTime < oneHour) {
      setFormState(prev => ({ 
        ...prev, 
        error: "Too many form submissions. Please try again later." 
      }));
      return;
    }
    
    // Input validation
    const validationError = validateInputs();
    if (validationError) {
      setFormState(prev => ({ ...prev, error: validationError }));
      return;
    }
    
    setFormState(prev => ({ ...prev, isSubmitting: true }));
    
    try {
      // Sanitize inputs (basic sanitization)
      const sanitizedData = {
        name: formState.name.trim(),
        email: formState.email.trim(),
        message: formState.message.trim(),
        // Add honeypot field to help catch spam bots
        _gotcha: ""
      };

      const response = await fetch('https://formspree.io/f/mldbobgy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(sanitizedData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Update submission count and time
        const newSubmitCount = formState.submitCount + 1;
        const newLastSubmitTime = Date.now();
        
        // Store submission data in session storage
        sessionStorage.setItem('formSubmissionData', JSON.stringify({
          submitCount: newSubmitCount,
          lastSubmitTime: newLastSubmitTime
        }));
        
        // Form submission was successful
        setFormState({
          name: '',
          email: '',
          message: '',
          isSubmitting: false,
          isSuccess: true,
          error: null,
          submitCount: newSubmitCount,
          lastSubmitTime: newLastSubmitTime
        });
      } else {
        // Server returned an error
        setFormState(prev => ({
          ...prev,
          isSubmitting: false,
          error: data.error || 'Something went wrong. Please try again.'
        }));
      }
    } catch (error) {
      setFormState(prev => ({
        ...prev,
        isSubmitting: false,
        error: 'Network error. Please check your connection and try again.'
      }));
    }
  };

  // Show success message when form is successfully submitted
  if (formState.isSuccess) {
    return (
      <div className={styles.contactForm}>
        <div className={styles.successMessage}>
          Thank you for your message! I'll get back to you soon.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.contactForm}>
      <form onSubmit={handleSubmit} className={styles.form}>
        {formState.error && (
          <div className={styles.errorMessage}>{formState.error}</div>
        )}
        
        <div className={styles.formGroup}>
          <label htmlFor="name" className={styles.formLabel}>Name</label>
          <input
            type="text"
            id="name"
            name="name"
            className={styles.formInput}
            placeholder="Your Name"
            value={formState.name}
            onChange={handleChange}
            maxLength="100"
            required
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="email" className={styles.formLabel}>Email</label>
          <input
            type="email"
            id="email"
            name="email"
            className={styles.formInput}
            placeholder="Your Email"
            value={formState.email}
            onChange={handleChange}
            maxLength="100"
            required
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="message" className={styles.formLabel}>Message</label>
          <textarea
            id="message"
            name="message"
            rows="5"
            className={styles.formTextArea}
            placeholder="Your Message"
            value={formState.message}
            onChange={handleChange}
            maxLength="3000"
            required
          />
        </div>
        
        {/* Honeypot field to catch spam bots */}
        <input
          type="text" 
          name="_gotcha" 
          style={{display: 'none'}}
          tabIndex="-1"
          autoComplete="off"
        />
        
        <button 
          type="submit" 
          className={styles.submitButton}
          disabled={formState.isSubmitting}
        >
          {formState.isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  );
}